import React, { useEffect, useState } from 'react';
import { Trophy, Star, Sparkles, X } from 'lucide-react';

const CONFETTI_COLORS = ['#00D3A8', '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'];

const Confetti = ({ count = 50 }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const newParticles = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 8 + Math.random() * 8,
      rotation: Math.random() * 360
    }));
    setParticles(newParticles);
  }, [count]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-confetti"
          style={{
            left: `${particle.x}%`,
            top: '-20px',
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`,
          }}
        >
          <div
            style={{
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
              transform: `rotate(${particle.rotation}deg)`,
              borderRadius: Math.random() > 0.5 ? '50%' : '0',
            }}
          />
        </div>
      ))}
      
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti-fall linear forwards;
        }
      `}</style>
    </div>
  );
};

const StarBurst = () => {
  return (
    <div className="absolute inset-0 pointer-events-none">
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="absolute left-1/2 top-1/2 animate-star-burst"
          style={{
            transform: `rotate(${i * 45}deg)`,
            animationDelay: `${i * 0.05}s`
          }}
        >
          <Star 
            size={16} 
            className="text-yellow-400 fill-yellow-400" 
            style={{ transform: 'translateX(60px)' }}
          />
        </div>
      ))}
      
      <style>{`
        @keyframes star-burst {
          0% {
            opacity: 0;
            transform: rotate(var(--rotation)) scale(0);
          }
          50% {
            opacity: 1;
            transform: rotate(var(--rotation)) scale(1.2);
          }
          100% {
            opacity: 0;
            transform: rotate(var(--rotation)) scale(0.8);
          }
        }
        .animate-star-burst {
          animation: star-burst 1s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

const BadgeCard = ({ badge, index }) => {
  const getLevelGradient = (level) => {
    switch (level) {
      case 'bronze': return 'from-amber-600 to-amber-400';
      case 'silver': return 'from-gray-400 to-gray-200';
      case 'gold': return 'from-yellow-500 to-yellow-300';
      case 'special': return 'from-pink-500 to-purple-500';
      default: return 'from-[#00D3A8] to-[#00B894]';
    }
  };

  return (
    <div 
      className="flex flex-col items-center animate-badge-appear"
      style={{ animationDelay: `${index * 0.2}s` }}
    >
      <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${getLevelGradient(badge.level)} 
        flex items-center justify-center text-4xl shadow-lg transform hover:scale-110 transition-transform
        ring-4 ring-white ring-opacity-50`}
      >
        {badge.icon}
      </div>
      <h3 className="mt-3 font-bold text-gray-900 text-center">{badge.name}</h3>
      <p className="text-sm text-gray-500 text-center mt-1">{badge.description}</p>
      {badge.xp && (
        <span className="mt-2 px-3 py-1 bg-[#00D3A8]/10 text-[#00D3A8] text-sm font-medium rounded-full">
          +{badge.xp} XP
        </span>
      )}
      
      <style>{`
        @keyframes badge-appear {
          0% {
            opacity: 0;
            transform: scale(0) rotate(-180deg);
          }
          60% {
            transform: scale(1.2) rotate(10deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        .animate-badge-appear {
          animation: badge-appear 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

const RewardPopup = ({ isOpen, onClose, badges = [] }) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Play sound effect (optional)
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleWxzb2xvb21ubG5ua21ubW1tbWxtbW1tbW1tbW1tbW1tbW1tbW1tbWxtbW1tbW1tbW1tbW1tbW1tbW1tbW1tbW5ub29ycHV4fYOJj5WboKasr7O3ur7CxsnM0NTX293g4+Xn6evs7u/x8vP09fb3+Pn6+/z8/f7+//8AAQECAwQFBgcICQoLDA0ODxAREhMUFRYXGBkaGxwdHh8gISIjJCUmJygpKissLS4vMDEyMzQ1Njc4OTo7PD0+P0BBQkNERUZHSElKS0xNTk9QUVJTVFVWV1hZWltcXV5fYGFiY2RlZmdoaWprbG1ub3BxcnN0dXZ3eHl6e3x9fn+AgYKDhIWGh4iJiouMjY6PkJGSk5SVlpeYmZqbnJ2en6ChoqOkpaanqKmqq6ytrq+wsbKztLW2t7i5uru8vb6/wMHCw8TFxsfIycrLzM3Oz9DR0tPU1dbX2Nna29zd3t/g4eLj5OXm5+jp6uvs7e7v8PHy8/T19vf4+fr7/P3+/w==');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {}
      
      // Stop confetti after animation
      const timer = setTimeout(() => setShowConfetti(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isInboxZero = badges.some(b => b.id === 'inbox_zero' || b.id === 'inbox_zero_celebration');

  return (
    <>
      {showConfetti && <Confetti count={80} />}
      
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4 animate-fade-in">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative overflow-hidden animate-popup-appear">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
          >
            <X size={20} />
          </button>
          
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#00D3A8]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-yellow-200/30 rounded-full blur-3xl" />
          </div>
          
          {/* Header */}
          <div className="relative text-center mb-6">
            <div className="relative inline-block">
              <div className="w-24 h-24 bg-gradient-to-br from-[#00D3A8] to-[#00B894] rounded-full flex items-center justify-center mx-auto shadow-lg animate-bounce-slow">
                {isInboxZero ? (
                  <Sparkles size={48} className="text-white" />
                ) : (
                  <Trophy size={48} className="text-white" />
                )}
              </div>
              <StarBurst />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mt-4 animate-slide-up">
              {isInboxZero ? '🎉 Félicitations !' : '🏆 Nouveau Badge !'}
            </h2>
            <p className="text-gray-500 mt-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              {isInboxZero 
                ? 'Vous avez atteint Inbox Zero !'
                : 'Vous avez débloqué une récompense !'
              }
            </p>
          </div>
          
          {/* Badges */}
          <div className={`grid gap-6 ${badges.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {badges.map((badge, index) => (
              <BadgeCard key={badge.id} badge={badge} index={index} />
            ))}
          </div>
          
          {/* Action button */}
          <button
            onClick={onClose}
            className="mt-8 w-full py-3 bg-gradient-to-r from-[#00D3A8] to-[#00B894] text-white font-semibold rounded-xl 
              hover:shadow-lg hover:scale-[1.02] transition-all duration-200 animate-slide-up"
            style={{ animationDelay: '0.3s' }}
          >
            Continuer
          </button>
          
          <style>{`
            @keyframes fade-in {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes popup-appear {
              from { 
                opacity: 0;
                transform: scale(0.9) translateY(20px);
              }
              to { 
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
            @keyframes slide-up {
              from {
                opacity: 0;
                transform: translateY(10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes bounce-slow {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-10px); }
            }
            .animate-fade-in {
              animation: fade-in 0.2s ease-out;
            }
            .animate-popup-appear {
              animation: popup-appear 0.4s ease-out;
            }
            .animate-slide-up {
              animation: slide-up 0.4s ease-out forwards;
              opacity: 0;
            }
            .animate-bounce-slow {
              animation: bounce-slow 2s ease-in-out infinite;
            }
          `}</style>
        </div>
      </div>
    </>
  );
};

export default RewardPopup;
